import { openAPIClient } from '../openApiClient';
import type {
  CertificateListResponse,
  CertificateListServiceConfig,
  CertificatePdfBatchResponse,
  CertificatePdfResponse,
  DeleteCertificateServiceConfig,
  GetCertificateCSVConfig,
  GetCertificatePdfBatchConfig,
  GetCertificatePdfByIdConfig,
  ResolveCertificateSubmitInfoResponse,
  ResolveCertificateSubmitInfoServiceConfig,
  ResubmitCertificateResponse,
  ResubmitCertificateServiceConfig,
  UpdateCertificateServiceConfig,
} from './types';

/**
 * A refused resubmission. Unlike the other services here, this endpoint answers
 * with a message that is already localized for the user, so the caller needs to
 * tell it apart from an incidental failure whose message is not presentable.
 */
export class ResubmitCertificateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ResubmitCertificateError';
  }
}

export async function getCertificateListService({
  httpRequestConfig,
  params,
}: CertificateListServiceConfig): Promise<CertificateListResponse> {
  const client = await openAPIClient(httpRequestConfig);
  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.GET('/v1/auth/certificate/{spaceId}', {
    params: {
      path: { spaceId: httpRequestConfig.space },
      query: params.query,
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

export async function deleteCertificateService({
  httpRequestConfig,
  params,
}: DeleteCertificateServiceConfig): Promise<void> {
  const client = await openAPIClient(httpRequestConfig);
  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.DELETE('/v1/auth/certificate/{spaceId}/{id}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.id,
      },
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }
}

export async function updateCertificateService({
  httpRequestConfig,
  params,
}: UpdateCertificateServiceConfig): Promise<void> {
  const client = await openAPIClient(httpRequestConfig);
  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.PUT('/v1/auth/certificate/{spaceId}/{id}', {
    params: {
      path: {
        id: params.params.id,
        spaceId: httpRequestConfig.space,
      },
      query: {
        language: params.query.language,
      },
    },
    body: {
      profileId: params.body.profileId,
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }
}

export async function getCertificatePDFByIdService({
  httpRequestConfig,
  params,
}: GetCertificatePdfByIdConfig): Promise<CertificatePdfResponse> {
  const client = await openAPIClient(httpRequestConfig);
  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.GET(
    '/v1/auth/certificateActions/getPdf/{spaceId}/{id}',
    {
      params: {
        path: {
          spaceId: httpRequestConfig.space,
          id: params.params.id,
        },
        query: {
          language: params.query.language,
        },
      },
    },
  );

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

export async function getCertificateCSVService({
  httpRequestConfig,
  params,
}: GetCertificateCSVConfig): Promise<string> {
  const client = await openAPIClient(httpRequestConfig);
  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.GET(
    '/v1/auth/certificateActions/getCsv/{spaceId}',
    {
      params: {
        path: { spaceId: httpRequestConfig.space },
        query: params.query,
      },
      parseAs: 'text',
    },
  );

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

export async function resolveCertificateSubmitInfoService({
  httpRequestConfig,
  params,
}: ResolveCertificateSubmitInfoServiceConfig): Promise<ResolveCertificateSubmitInfoResponse> {
  const client = await openAPIClient(httpRequestConfig);
  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.POST(
    '/v1/auth/certificateActions/resolveCertificateSubmitInfo/{spaceId}/{id}',
    {
      params: {
        path: {
          spaceId: httpRequestConfig.space,
          id: params.params.id,
        },
        query: {
          language: params.query.language,
        },
      },
    },
  );

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

export async function getCertificatePdfBatchService({
  httpRequestConfig,
  params,
}: GetCertificatePdfBatchConfig): Promise<CertificatePdfBatchResponse> {
  const client = await openAPIClient(httpRequestConfig);
  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.POST(
    '/v1/auth/certificateActions/getPdfBatch/{spaceId}',
    {
      params: {
        path: { spaceId: httpRequestConfig.space },
        query: { language: params.query.language },
      },
      body: { ids: params.body.ids },
    },
  );

  if (response.error !== undefined) {
    const error = new Error(response.error.message);
    // Preserve error code for proper error handling
    if ('code' in response.error) {
      (error as Error & { code?: string }).code = response.error.code as string;
    }
    throw error;
  }

  return response.data;
}

export async function resubmitCertificateService({
  httpRequestConfig,
  params,
}: ResubmitCertificateServiceConfig): Promise<ResubmitCertificateResponse> {
  const client = await openAPIClient(httpRequestConfig);
  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.POST(
    '/v1/auth/certificateActions/resubmitCertificate/{spaceId}/{id}',
    {
      params: {
        path: {
          spaceId: httpRequestConfig.space,
          id: params.params.id,
        },
        query: {
          language: params.query.language,
        },
      },
    },
  );

  if (response.error !== undefined) {
    throw new ResubmitCertificateError(response.error.message);
  }

  return response.data;
}
